import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import DOMPurify from 'dompurify';
import { useCourse } from '../context/CourseContext';
import { AudioPlayer } from '../components/course/AudioPlayer';
import { MeditationPlayer } from '../components/course/MeditationPlayer';
import { SET_POINT_TRACKS } from '../data/courseMeditations';
import { JournalEntry } from '../components/course/JournalEntry';
import { TableOfContents } from '../components/course/TableOfContents';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Clock,
  Headphones,
  Bookmark,
  BookmarkCheck,
  CheckCircle2,
  Share2,
  ChevronUp,
  Lightbulb,
} from 'lucide-react';

// Format chapter content with proper markdown rendering
const formatChapterContent = (content) => {
  if (!content) return '';
  
  let html = content;
  let headingIndex = 0;
  
  // Helper to generate IDs
  const generateId = (text, index) => {
    return `section-${text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50)}-${index}`;
  };
  
  // Process code blocks first (to preserve them)
  html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre class="bg-muted/50 p-4 rounded-lg overflow-x-auto my-6 text-sm"><code>$2</code></pre>');
  
  // Process tables
  html = html.replace(/\|(.+)\|/g, (match) => {
    const cells = match.split('|').filter(c => c.trim());
    if (cells.some(c => c.trim().match(/^[-:]+$/))) {
      return ''; // Skip separator rows
    }
    const isHeader = match.includes('---');
    const cellTag = isHeader ? 'th' : 'td';
    const cellClass = isHeader 
      ? 'px-4 py-3 text-left font-semibold text-foreground bg-muted/50' 
      : 'px-4 py-3 text-left text-muted-foreground border-t border-silver/20';
    return `<tr>${cells.map(c => `<${cellTag} class="${cellClass}">${c.trim()}</${cellTag}>`).join('')}</tr>`;
  });
  
  // Wrap tables
  html = html.replace(/(<tr>[\s\S]*?<\/tr>)+/g, '<div class="overflow-x-auto my-8"><table class="w-full border border-silver/20 rounded-lg overflow-hidden">$&</table></div>');
  
  // Process headings with IDs for TOC navigation
  html = html.replace(/^## (.+)$/gm, (match, text) => {
    const id = generateId(text, headingIndex++);
    return `<h2 id="${id}" class="font-display text-2xl font-bold text-foreground mt-12 mb-6 pb-2 border-b border-silver/20 scroll-mt-28">${text}</h2>`;
  });
  
  html = html.replace(/^### (.+)$/gm, (match, text) => {
    const id = generateId(text, headingIndex++);
    return `<h3 id="${id}" class="font-display text-xl font-semibold text-maroon mt-10 mb-4 scroll-mt-28">${text}</h3>`;
  });
  
  // Process bold text
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>');
  
  // Process italic text
  html = html.replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');
  
  // Process blockquotes - style them as inspirational quotes
  html = html.replace(/^> "(.+)"$/gm, '<blockquote class="pl-6 border-l-4 border-secondary bg-gradient-to-r from-secondary/10 to-transparent py-5 pr-6 my-8 rounded-r-lg"><p class="text-lg italic text-foreground font-medium">"$1"</p></blockquote>');
  html = html.replace(/^> (.+)$/gm, '<blockquote class="pl-6 border-l-4 border-secondary bg-secondary/5 py-4 pr-4 my-6 rounded-r-lg italic text-foreground">$1</blockquote>');
  
  // Process journal lines (underscores for note-taking)
  html = html.replace(/^_{20,}$/gm, '<div class="journal-line border-b border-silver/40 min-h-[2.5rem] my-2 hover:border-maroon/40 transition-colors"></div>');
  
  // Process numbered lists
  html = html.replace(/^(\d+)\. (.+)$/gm, '<li class="numbered-item mb-3 pl-2" data-number="$1"><span class="text-foreground leading-relaxed">$2</span></li>');
  
  // Process bullet lists (- items)
  html = html.replace(/^- (.+)$/gm, '<li class="bullet-item mb-3 pl-2 flex items-start gap-3"><span class="w-2 h-2 rounded-full bg-maroon flex-shrink-0 mt-2"></span><span class="text-foreground leading-relaxed">$1</span></li>');
  
  // Wrap consecutive list items in ul
  html = html.replace(/(<li class="bullet-item[\s\S]*?<\/li>\n?)+/g, '<ul class="my-6 space-y-1 list-none">$&</ul>');
  html = html.replace(/(<li class="numbered-item[\s\S]*?<\/li>\n?)+/g, '<ol class="my-6 space-y-1 list-none counter-reset-item">$&</ol>');
  
  // Process links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-maroon hover:text-maroon-light underline underline-offset-4 transition-colors" target="_blank" rel="noopener noreferrer">$1</a>');
  
  // Process horizontal rules
  html = html.replace(/^---$/gm, '<hr class="my-10 border-t border-silver/30" />');
  
  // Process paragraphs (double newlines)
  html = html.replace(/\n\n/g, '</p><p class="mb-6 leading-relaxed text-foreground">');
  
  // Clean up empty paragraphs and fix structure
  html = html.replace(/<p class="[^"]*"><\/p>/g, '');
  html = html.replace(/<p class="[^"]*">(\s*)<(h[23]|ul|ol|blockquote|hr|div|pre)/g, '<$2');
  html = html.replace(/<\/(h[23]|ul|ol|blockquote|hr|div|pre)>(\s*)<\/p>/g, '</$1>');
  
  // Wrap content in starting paragraph if needed
  if (!html.startsWith('<')) {
    html = '<p class="mb-6 leading-relaxed text-foreground">' + html;
  }
  
  return html;
};

export const ChapterDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    chapters,
    getChapterProgress,
    markChapterStarted,
    markChapterComplete,
    updateReadingProgress,
    toggleBookmark,
    isBookmarked,
    currentChapterId,
  } = useCourse();

  const contentRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);

  const chapterId = parseInt(id, 10);
  const chapter = chapters.find(ch => ch.id === chapterId);
  const progress = getChapterProgress(chapterId);
  
  // Calculate next and prev chapters
  const currentIndex = chapters.findIndex(ch => ch.id === chapterId);
  const nextChapter = currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : null;
  const prevChapter = currentIndex > 0 ? chapters[currentIndex - 1] : null;

  // Mark chapter as started when viewing
  useEffect(() => {
    if (chapter && chapterId) {
      markChapterStarted(chapterId);
    }
    // markChapterStarted is from context — intentionally NOT in deps to avoid
    // infinite re-render (it's recreated on every CourseProvider render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId, chapter]);

  // Track reading progress
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const element = contentRef.current;
      const windowHeight = window.innerHeight;
      const documentHeight = element.scrollHeight;
      const scrollTop = window.scrollY;
      
      // Calculate progress
      const totalScrollable = documentHeight - windowHeight;
      const scrollProgress = Math.min((scrollTop / totalScrollable) * 100, 100);
      setReadingProgress(Math.round(scrollProgress));
      updateReadingProgress(chapterId, Math.round(scrollProgress));

      // Show/hide scroll to top button
      setShowScrollTop(scrollTop > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // updateReadingProgress is from context — intentionally omitted (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCompleteChapter = () => {
    markChapterComplete(chapterId);
    toast.success('Chapter completed! 🎉', {
      description: 'Great job on finishing this chapter.',
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${chapter.step}: ${chapter.title}`,
        text: chapter.summary,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (!chapter) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-foreground mb-4">
            Chapter Not Found
          </h1>
          <Link to="/chapters">
            <Button className="btn-teal">Back to Chapters</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div ref={contentRef} className="min-h-screen bg-gradient-to-b from-background to-silver-shimmer/5">
      {/* Reading Progress Bar */}
      <div className="fixed top-16 left-0 right-0 z-40 h-1 bg-muted">
        <motion.div
          className="h-full bg-gradient-to-r from-primary via-maroon to-secondary"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* Chapter Hero */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img
          src={chapter.image}
          alt={chapter.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-maroon/10" />
        
        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto px-4 pb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Step Badge */}
              <Badge className="bg-maroon text-white mb-4 shadow-maroon">
                {chapter.step}
              </Badge>

              {/* Title */}
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-3">
                {chapter.title}
              </h1>

              {/* Subtitle */}
              <p className="text-lg text-muted-foreground mb-6 max-w-2xl">
                {chapter.subtitle}
              </p>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  {chapter.duration}
                </span>
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Headphones className="w-4 h-4" />
                  {chapter.audioLength} audio
                </span>
                {progress.completed && (
                  <Badge className="bg-success text-success-foreground gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Completed
                  </Badge>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Action Bar */}
      <div className="sticky top-[68px] z-30 bg-background/95 backdrop-blur border-b border-silver/20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-2">
              <Link to="/chapters">
                <Button variant="ghost" size="sm" className="gap-1 hover:text-primary">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">All Chapters</span>
                </Button>
              </Link>
              {prevChapter && (
                <Link to={`/chapter/${prevChapter.id}`}>
                  <Button variant="ghost" size="sm" className="hover:text-primary">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                </Link>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleBookmark(chapterId)}
                className="hover:text-secondary"
              >
                {isBookmarked(chapterId) ? (
                  <BookmarkCheck className="w-4 h-4 text-secondary" />
                ) : (
                  <Bookmark className="w-4 h-4" />
                )}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleShare} className="hover:text-maroon">
                <Share2 className="w-4 h-4" />
              </Button>
              {nextChapter && (
                <Link to={`/chapter/${nextChapter.id}`}>
                  <Button variant="ghost" size="sm" className="hover:text-primary">
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents Sidebar */}
      <TableOfContents content={chapter.content} />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Audio Player */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <AudioPlayer chapter={chapter} />
          </motion.div>

          {/* Chapter Content */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="chapter-content mb-12"
          >
            <div 
              className="text-foreground"
              // SECURITY: Content is sanitized via DOMPurify before injection.
              // formatChapterContent() returns markdown converted to HTML strings,
              // then DOMPurify strips any unsafe tags/attrs (XSS-safe).
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(formatChapterContent(chapter.content))
              }}
            />
          </motion.article>

          {/* 8 Emotional Set Point Meditations — Chapter 3 only */}
          {chapter.id === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mb-12"
              data-testid="chapter3-set-point-meditations"
            >
              <MeditationPlayer
                tracks={SET_POINT_TRACKS}
                title="The 8 Emotional Set Point Meditations"
                subtitle="Choose the set point you're working with — each meditation rewires that specific pattern at the nervous-system level."
              />
            </motion.div>
          )}

          {/* Key Takeaways */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-silver-shimmer/20 to-background rounded-xl p-6 mb-12 border border-silver/20 shadow-silver"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-maroon/20 flex items-center justify-center shadow-maroon">
                <Lightbulb className="w-5 h-5 text-maroon" />
              </div>
              <h3 className="font-display text-xl font-semibold text-foreground">
                Key Takeaways
              </h3>
            </div>
            <ul className="space-y-3">
              {chapter.keyTakeaways.map((takeaway, idx) => (
                <li key={`takeaway-${chapter.id}-${idx}`} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{takeaway}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <Separator className="my-12" />

          {/* Journal Entry */}
          <JournalEntry chapter={chapter} />

          <Separator className="my-12" />

          {/* Chapter Navigation */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-stretch">
            {prevChapter ? (
              <Link to={`/chapter/${prevChapter.id}`} className="flex-1">
                <Button variant="outline" className="w-full h-full min-h-[80px] justify-start text-left p-4">
                  <ArrowLeft className="w-5 h-5 mr-3 flex-shrink-0" />
                  <div>
                    <span className="text-xs text-muted-foreground block">Previous</span>
                    <span className="font-medium line-clamp-1">{prevChapter.title}</span>
                  </div>
                </Button>
              </Link>
            ) : (
              <div className="flex-1" />
            )}

            {!progress.completed && (
              <Button 
                onClick={handleCompleteChapter}
                className="btn-gold gap-2 px-8"
              >
                <CheckCircle2 className="w-5 h-5" />
                Mark Complete
              </Button>
            )}

            {nextChapter ? (
              <Link to={`/chapter/${nextChapter.id}`} className="flex-1">
                <Button className="w-full h-full min-h-[80px] btn-teal justify-end text-right p-4">
                  <div>
                    <span className="text-xs opacity-80 block">Next Chapter</span>
                    <span className="font-medium line-clamp-1">{nextChapter.title}</span>
                  </div>
                  <ArrowRight className="w-5 h-5 ml-3 flex-shrink-0" />
                </Button>
              </Link>
            ) : progress.completed ? (
              <Link to="/progress" className="flex-1">
                <Button className="w-full h-full min-h-[80px] btn-gold justify-center p-4">
                  <div className="text-center">
                    <span className="text-xs opacity-80 block">You did it!</span>
                    <span className="font-medium">View Your Progress</span>
                  </div>
                </Button>
              </Link>
            ) : (
              <div className="flex-1" />
            )}
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 w-12 h-12 rounded-full bg-maroon text-white shadow-maroon flex items-center justify-center hover:bg-maroon-light transition-colors z-40"
          >
            <ChevronUp className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Floating Audio Player */}
      <AudioPlayer chapter={chapter} minimized />
    </div>
  );
};

export default ChapterDetailPage;
